'use client';

import { useEffect, useRef } from 'react';

interface SplitHeadingRevealProps {
  className?: string;
  children: React.ReactNode;
}

/** Section heading that clip-reveals line by line on scroll (GSAP SplitText).
 *  Renders the plain heading immediately for SSR/no-JS; the reveal is a
 *  progressive client-side enhancement. Honors prefers-reduced-motion. */
export function SplitHeadingReveal({ className, children }: SplitHeadingRevealProps) {
  const ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    let ctx: { revert: () => void } | undefined;
    let cancelled = false;

    (async () => {
      const { gsap, SplitText } = await import('@/lib/gsap');
      if (cancelled || !ref.current) return;

      ctx = gsap.context(() => {
        const split = SplitText.create(ref.current, { type: 'lines', mask: 'lines' });
        gsap.set(split.lines, { yPercent: 100, opacity: 0 });
        gsap.to(split.lines, {
          yPercent: 0,
          opacity: 1,
          duration: 0.9,
          stagger: 0.08,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: ref.current,
            start: 'top 85%',
            once: true,
          },
        });
      });
    })();

    return () => {
      cancelled = true;
      ctx?.revert();
    };
  }, []);

  return (
    <h2 ref={ref} className={className}>
      {children}
    </h2>
  );
}
